import type { OrderId, OrderRow } from '../model/order-schema';

/**
 * Night search alarm — the pure half of cpanel `model/searchAlarm.ts`. An
 * order "overdue in search" has been looking for a courier longer than
 * `SEARCH_OVERDUE_MS`; crossing it escalates the page-wide alarm.
 */

/** Search time after which an order counts as overdue (4 minutes). */
export const SEARCH_OVERDUE_MS = 4 * 60 * 1000;

/** How often the monitor re-fetches the in-search list (10 seconds). */
export const SEARCH_ALARM_POLL_MS = 10 * 1000;

/** The in-search poll's page size and rolling window (cpanel: 1000 rows, 24 h). */
export const SEARCH_ALARM_LIMIT = 1000;
export const SEARCH_ALARM_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * The alarm is armed only at night, when courier coverage is thinnest. The
 * window WRAPS midnight: 21:00 (inclusive) through 09:00 (exclusive), in the
 * viewer's LOCAL time.
 */
export const NIGHT_ALARM_START_HOUR = 21;
export const NIGHT_ALARM_END_HOUR = 9;

export function isNightAlarmWindow(now: number | Date = Date.now()): boolean {
  const hour = (now instanceof Date ? now : new Date(now)).getHours();
  return hour >= NIGHT_ALARM_START_HOUR || hour < NIGHT_ALARM_END_HOUR;
}

export interface OverdueSearchOrder {
  id: OrderId;
  displayId: OrderId;
  /** How long, in ms, the order has been searching as of `now`. */
  searchingMs: number;
}

/**
 * When an order's search clock started: `search_started_at`, falling back to
 * `created_at` for rows that predate it. NaN when neither parses.
 */
export function getSearchStartMs(
  order: Pick<OrderRow, 'search_started_at' | 'created_at'>,
): number {
  const raw = order.search_started_at ?? order.created_at;
  if (!raw) return NaN;
  return new Date(raw).getTime();
}

/**
 * The in-search orders searching for at least `SEARCH_OVERDUE_MS`, longest
 * first. Rows with a `search_stopped_at` are skipped. Outside the night window
 * this is always empty, which stands the whole alarm down — the poll keeps
 * running so the alarm is accurate the moment 21:00 arrives.
 */
export function findOverdueSearchOrders(
  orders: readonly Pick<
    OrderRow,
    'id' | 'display_id' | 'search_started_at' | 'search_stopped_at' | 'created_at'
  >[],
  now: number = Date.now(),
): OverdueSearchOrder[] {
  if (!isNightAlarmWindow(now)) return [];

  const overdue: OverdueSearchOrder[] = [];
  for (const order of orders) {
    if (order.search_stopped_at) continue;
    const startedMs = getSearchStartMs(order);
    if (Number.isNaN(startedMs)) continue;
    const searchingMs = now - startedMs;
    if (searchingMs >= SEARCH_OVERDUE_MS) {
      overdue.push({
        id: order.id,
        displayId: order.display_id ?? order.id,
        searchingMs,
      });
    }
  }
  return overdue.sort((a, b) => b.searchingMs - a.searchingMs);
}

/** Minutes + zero-padded seconds of a duration (`{ minutes: 5, seconds: '07' }`). */
export function splitSearchingDuration(ms: number): { minutes: number; seconds: string } {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  return {
    minutes: Math.floor(totalSeconds / 60),
    seconds: (totalSeconds % 60).toString().padStart(2, '0'),
  };
}
